// ============================================================================
// 建物データ取得 / Project PLATEAU 3D都市モデル(自前ビルドPMTiles)
// ----------------------------------------------------------------------------
// PLATEAUのCityGML(bldg:Building)から抽出した「フットプリント(lod0RoofEdge、
// 無ければlod1Solidの1面)＋measuredHeight(実測高さ)」を、OSM版と同じz12
// 単一ズームでtippecanoeビルドしたPMTiles。GSI/MS版と違い高さが実測値な
// ため、estimatedは常にfalse。
//
// PLATEAUは全国ではなく整備済みの一部自治体(2025年度時点で約300都市)のみ
// カバーする点が既存3ソースと異なる(範囲外の地域はゼロ件になる)。
// ============================================================================

import { VectorTile } from "@mapbox/vector-tile";
import { PbfReader } from "pbf";
import { distanceMeters } from "./geo";
import type { Building, LngLat } from "./types";
import {
  TILE_ZOOM,
  getPMTiles,
  localToLngLat,
  ringArea,
  ringIntersectsBbox,
  tileBboxDistance,
  tilesForBbox,
} from "./pmtilesCommon";

/** Cloudflare R2(パブリックバケット、CORS設定済み)でホスティング。既存3種と同じバケット。 */
const TILE_URL = "https://pub-aac031ce777c439bbb845a5887dd9b21.r2.dev/japan-plateau-buildings.pmtiles";

// buildingTiles.tsと同じ安全弁(都心の高密度タイル対策)。
const MAX_DECODED_FEATURES = 300000;

/**
 * bbox [south, west, north, east] にかかるz12タイルをすべて取得し、
 * buildingレイヤーをBuilding[]に変換する。
 */
export async function fetchPlateauBuildingsFromTiles(
  south: number,
  west: number,
  north: number,
  east: number
): Promise<Building[]> {
  const p = getPMTiles(TILE_URL);
  const center: LngLat = [(west + east) / 2, (south + north) / 2];
  const tileCoords = tilesForBbox(south, west, north, east, TILE_ZOOM).sort(
    (a, b) =>
      tileBboxDistance(center, a.tx, a.ty, TILE_ZOOM) -
      tileBboxDistance(center, b.tx, b.ty, TILE_ZOOM)
  );

  const tileBytes = await Promise.all(tileCoords.map(({ tx, ty }) => p.getZxy(TILE_ZOOM, tx, ty)));

  const candidates: (Building & { dist: number })[] = [];
  let nextId = 1;
  for (let t = 0; t < tileCoords.length; t++) {
    if (candidates.length >= MAX_DECODED_FEATURES) break;
    const res = tileBytes[t];
    if (!res) continue;
    const { tx, ty } = tileCoords[t];
    const tile = new VectorTile(new PbfReader(new Uint8Array(res.data)));
    const layer = tile.layers.buildings;
    if (!layer) continue;
    for (let i = 0; i < layer.length; i++) {
      const f = layer.feature(i);
      const rings = f
        .loadGeometry()
        .map((pts) =>
          pts.map((pt): LngLat => localToLngLat(pt.x, pt.y, tx, ty, TILE_ZOOM, f.extent))
        )
        .filter((ring) => ring.length >= 3);
      if (rings.length === 0) continue;
      const outer = rings.reduce((a, b) => (ringArea(b) > ringArea(a) ? b : a));
      if (!ringIntersectsBbox(outer, south, west, north, east)) continue;
      const height = Number(f.properties.height) || 3;
      candidates.push({
        id: nextId++,
        ring: outer,
        height,
        estimated: false,
        dist: distanceMeters(center, outer[0]),
      });
    }
  }
  candidates.sort((a, b) => a.dist - b.dist);
  return candidates.slice(0, MAX_DECODED_FEATURES).map(({ dist: _dist, ...b }) => b);
}
